import { combineLabel, leadLabel } from '../repeat/rule.js'
import { JoinedPill, SlotCount, SlotDot, SlotKey } from '../repeat/ServiceTracks.js'
import { Button, Card, CardHeader } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'
import { BILLING_LABEL } from './builder-model.js'

/**
 * Beside the agreement's sites and services: what it is — its term, its
 * billing, how its jobs are made and joined, who it is with — then its notes
 * and the key to the marks. Here rather than under the list, where on a
 * council of 168 sites nobody scrolled far enough to find them.
 */
export function AgreementRail({
  page,
  term,
  termSub,
  onEdit,
}: {
  page: AgreementPage
  term: string
  termSub: string
  /** Absent for somebody who cannot edit the agreement. */
  onEdit?: () => void
}) {
  const lead = leadLabel(page.leadDays)
  const together = combineLabel(page)
  const edit = (what: string) =>
    onEdit ? (
      <Button size="sm" variant="ghost" aria-label={`Edit ${what}`} onClick={onEdit}>
        Edit
      </Button>
    ) : undefined

  return (
    <aside aria-label="About this agreement" className="flex min-w-0 flex-col gap-[var(--stack-gap)] xl:sticky xl:top-[var(--page-pad)]">
      <Card>
        <CardHeader title="Agreement" action={edit('the agreement')} />
        <dl className="grid gap-2.5 p-[var(--card-pad)]">
          <Fact term="Term" sub={termSub}>
            {term}
          </Fact>
          <Fact term="Billing" sub="Recorded only. Nothing is invoiced from it yet.">
            {BILLING_LABEL[page.billing]}
          </Fact>
          <Fact term="Jobs" sub={page.autoCreate ? 'Made automatically' : 'Automatic creation is off'}>
            {page.autoCreate ? `Created ${lead} before they’re due` : `Made by hand from ${lead} before they’re due`}
          </Fact>
          <Fact term="One job" sub={together ? 'With every service and its instructions' : null}>
            {together ? `Visits at a site due ${together}` : 'Each visit is a job of its own'}
          </Fact>
          {page.contact ? (
            <Fact term="Contact" sub={page.contact.position}>
              {page.contact.name}
            </Fact>
          ) : null}
        </dl>
      </Card>

      <Card>
        <CardHeader title="Notes" action={edit('the notes')} />
        <p className="p-[var(--card-pad)] text-[13px] whitespace-pre-wrap text-ink-800">
          {page.notes || <span className="text-ink-400">No notes.</span>}
        </p>
      </Card>

      <Card className="p-[var(--card-pad)]">
        <h2 className="mb-2 font-display text-[15px] leading-snug font-bold text-brand-800">Key</h2>
        <SlotKey look="dots" marks={['done', 'booked', 'planned', 'overdue', 'paused']} className="gap-x-4 gap-y-1.5" />
        <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-ink-600">
          <span aria-hidden="true" className="grid w-4 place-items-center">
            <SlotCount marks={['planned', 'planned', 'planned']} />
          </span>
          Visits that month, on a folded line
        </p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-ink-600">
          <span aria-hidden="true" className="grid place-items-center">
            <JoinedPill>
              <SlotDot mark="planned" small look="dots" />
              <SlotDot mark="planned" small look="dots" />
            </JoinedPill>
          </span>
          Made as one job, joined by a line on an open site
        </p>
      </Card>
    </aside>
  )
}

function Fact({ term, sub, children }: { term: string; sub: string | null; children: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">{term}</dt>
      <dd className="mt-0.5 text-[13px] text-ink-900">{children}</dd>
      {sub ? <dd className="text-[11.5px] text-ink-500">{sub}</dd> : null}
    </div>
  )
}
